import React, { useState, useEffect, useMemo } from 'react';
import { Search, Calculator, Euro, FileText, ExternalLink, Clock, User, MapPin, Calendar, Info, Loader2, AlertCircle, BookOpen, Stethoscope, Mic } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { VoiceInput } from '../components/VoiceInput';
import { PageHeader } from '../components/PageHeader';
import { AIResultSections } from '../components/AIResultSections';

const API_URL = '';

// Base de données des actes courants en médecine générale
// CODES VÉRIFIÉS - Source: Ameli CCAM/NGAP - TARIFS 2025 (Convention décembre 2024)
// NOTE: les tarifs NGAP (consultations/majorations) proviennent désormais du backend
// (source unique datée: GET /api/cotation/ngap), aussi utilisée par l'IA. Ci-dessous: actes CCAM curatés.
const CCAM_ACTES = [
  
  // ═══════════════════════════════════════════════════════════
  // ACTES CCAM - ECG/CARDIOLOGIE
  // ═══════════════════════════════════════════════════════════
  { code: 'DEQP003', libelle: 'ECG sur 12 dérivations', tarif: 13.52, type: 'ccam', lien: 'https://www.ameli.fr/accueil-de-la-ccam/trouver-un-acte/fiche-abregee.php?code=DEQP003' },
  
  // ═══════════════════════════════════════════════════════════
  // ACTES CCAM - ÉPILATION
  // ═══════════════════════════════════════════════════════════
  { code: 'QZNP027', libelle: 'Épilation électrique (électrolyse)', tarif: 28.80, type: 'ccam', lien: 'https://www.ameli.fr/accueil-de-la-ccam/trouver-un-acte/fiche-abregee.php?code=QZNP027' },
  { code: 'QZNP029', libelle: 'Épilation laser/lumière pulsée < 50 cm²', tarif: 28.80, type: 'ccam', lien: 'https://www.ameli.fr/accueil-de-la-ccam/trouver-un-acte/fiche-abregee.php?code=QZNP029' },
  { code: 'QZNP028', libelle: 'Épilation laser/lumière pulsée 50-150 cm²', tarif: 36.12, type: 'ccam', lien: 'https://www.ameli.fr/accueil-de-la-ccam/trouver-un-acte/fiche-abregee.php?code=QZNP028' },
  { code: 'QZNP030', libelle: 'Épilation laser/lumière pulsée > 150 cm²', tarif: 43.43, type: 'ccam', lien: 'https://www.ameli.fr/accueil-de-la-ccam/trouver-un-acte/fiche-abregee.php?code=QZNP030' },
  
  // ═══════════════════════════════════════════════════════════
  // ACTES CCAM - DESTRUCTION LÉSIONS
  // ═══════════════════════════════════════════════════════════
  { code: 'QZFA007', libelle: 'Destruction lésion < 1 cm (azote/électrocoag)', tarif: 20.90, type: 'ccam', lien: 'https://www.ameli.fr/accueil-de-la-ccam/trouver-un-acte/fiche-abregee.php?code=QZFA007' },
  { code: 'QZFA008', libelle: 'Destruction lésion 1-2 cm', tarif: 31.36, type: 'ccam', lien: 'https://www.ameli.fr/accueil-de-la-ccam/trouver-un-acte/fiche-abregee.php?code=QZFA008' },
  { code: 'QZFA009', libelle: 'Destruction lésion > 2 cm', tarif: 41.80, type: 'ccam', lien: 'https://www.ameli.fr/accueil-de-la-ccam/trouver-un-acte/fiche-abregee.php?code=QZFA009' },
  { code: 'QZFA017', libelle: 'Destruction lésions multiples (verrues)', tarif: 41.80, type: 'ccam', lien: 'https://www.ameli.fr/accueil-de-la-ccam/trouver-un-acte/fiche-abregee.php?code=QZFA017' },
  { code: 'QZFA019', libelle: 'Destruction verrue unique', tarif: 20.90, type: 'ccam', lien: 'https://www.ameli.fr/accueil-de-la-ccam/trouver-un-acte/fiche-abregee.php?code=QZFA019' },
  
  // ═══════════════════════════════════════════════════════════
  // ACTES CCAM - BIOPSIES
  // ═══════════════════════════════════════════════════════════
  { code: 'QZHA001', libelle: 'Biopsie cutanée (punch)', tarif: 26.88, type: 'ccam', lien: 'https://www.ameli.fr/accueil-de-la-ccam/trouver-un-acte/fiche-abregee.php?code=QZHA001' },
  { code: 'QZHA002', libelle: 'Biopsie cutanée avec suture', tarif: 40.32, type: 'ccam', lien: 'https://www.ameli.fr/accueil-de-la-ccam/trouver-un-acte/fiche-abregee.php?code=QZHA002' },
  
  // ═══════════════════════════════════════════════════════════
  // ACTES CCAM - ONGLE
  // ═══════════════════════════════════════════════════════════
  { code: 'QZGA001', libelle: 'Avulsion partielle ongle', tarif: 25.52, type: 'ccam', lien: 'https://www.ameli.fr/accueil-de-la-ccam/trouver-un-acte/fiche-abregee.php?code=QZGA001' },
  { code: 'QZGA002', libelle: 'Avulsion totale ongle', tarif: 38.40, type: 'ccam', lien: 'https://www.ameli.fr/accueil-de-la-ccam/trouver-un-acte/fiche-abregee.php?code=QZGA002' },
  { code: 'QZJA011', libelle: 'Exérèse matricielle partielle (ongle incarné)', tarif: 52.25, type: 'ccam', lien: 'https://www.ameli.fr/accueil-de-la-ccam/trouver-un-acte/fiche-abregee.php?code=QZJA011' },
  { code: 'QZJA012', libelle: 'Exérèse matricielle totale', tarif: 78.37, type: 'ccam', lien: 'https://www.ameli.fr/accueil-de-la-ccam/trouver-un-acte/fiche-abregee.php?code=QZJA012' },
  
  // ═══════════════════════════════════════════════════════════
  // ACTES CCAM - PROCTOLOGIE
  // ═══════════════════════════════════════════════════════════
  { code: 'EGFA004', libelle: 'Incision abcès anal superficiel', tarif: 20.90, type: 'ccam', lien: 'https://www.ameli.fr/accueil-de-la-ccam/trouver-un-acte/fiche-abregee.php?code=EGFA004' },
  { code: 'EJFA001', libelle: 'Incision thrombose hémorroïdaire', tarif: 31.36, type: 'ccam', lien: 'https://www.ameli.fr/accueil-de-la-ccam/trouver-un-acte/fiche-abregee.php?code=EJFA001' },
  
  // ═══════════════════════════════════════════════════════════
  // ACTES CCAM - ORTHOPÉDIE
  // ═══════════════════════════════════════════════════════════
  { code: 'MZMP001', libelle: 'Attelle plâtrée membre supérieur', tarif: 26.88, type: 'ccam', lien: 'https://www.ameli.fr/accueil-de-la-ccam/trouver-un-acte/fiche-abregee.php?code=MZMP001' },
  { code: 'MZMP002', libelle: 'Attelle plâtrée membre inférieur', tarif: 40.32, type: 'ccam', lien: 'https://www.ameli.fr/accueil-de-la-ccam/trouver-un-acte/fiche-abregee.php?code=MZMP002' },
  { code: 'MZMP003', libelle: 'Strapping/contention souple', tarif: 20.90, type: 'ccam', lien: 'https://www.ameli.fr/accueil-de-la-ccam/trouver-un-acte/fiche-abregee.php?code=MZMP003' },
  { code: 'MZJB001', libelle: 'Ponction articulaire (genou, épaule)', tarif: 26.88, type: 'ccam', lien: 'https://www.ameli.fr/accueil-de-la-ccam/trouver-un-acte/fiche-abregee.php?code=MZJB001' },
  { code: 'MZLB001', libelle: 'Infiltration périarticulaire', tarif: 20.90, type: 'ccam', lien: 'https://www.ameli.fr/accueil-de-la-ccam/trouver-un-acte/fiche-abregee.php?code=MZLB001' },
  
  // ═══════════════════════════════════════════════════════════
  // ACTES CCAM - UROLOGIE
  // ═══════════════════════════════════════════════════════════
  { code: 'JVJF001', libelle: 'Pose sonde urinaire', tarif: 20.90, type: 'ccam', lien: 'https://www.ameli.fr/accueil-de-la-ccam/trouver-un-acte/fiche-abregee.php?code=JVJF001' },
  { code: 'JVJF002', libelle: 'Changement sonde urinaire', tarif: 10.08, type: 'ccam', lien: 'https://www.ameli.fr/accueil-de-la-ccam/trouver-un-acte/fiche-abregee.php?code=JVJF002' },
  { code: 'JCGE001', libelle: 'Décalottage/réduction paraphimosis', tarif: 26.88, type: 'ccam', lien: 'https://www.ameli.fr/accueil-de-la-ccam/trouver-un-acte/fiche-abregee.php?code=JCGE001' },
  
  // ═══════════════════════════════════════════════════════════
  // ACTES CCAM - PNEUMOLOGIE
  // ═══════════════════════════════════════════════════════════
  { code: 'GLQP012', libelle: 'Spirométrie lente et forcée', tarif: 40.28, type: 'ccam', lien: 'https://www.ameli.fr/accueil-de-la-ccam/trouver-un-acte/fiche-abregee.php?code=GLQP012' },
  { code: 'GLQP008', libelle: 'Spirométrie simple', tarif: 26.88, type: 'ccam', lien: 'https://www.ameli.fr/accueil-de-la-ccam/trouver-un-acte/fiche-abregee.php?code=GLQP008' },
  
  // ═══════════════════════════════════════════════════════════
  // ACTES CCAM - INJECTIONS
  // ═══════════════════════════════════════════════════════════
  { code: 'QZJA002', libelle: 'Injection intramusculaire', tarif: 3.15, type: 'ccam', lien: 'https://www.ameli.fr/accueil-de-la-ccam/trouver-un-acte/fiche-abregee.php?code=QZJA002' },
  { code: 'QZJA003', libelle: 'Injection sous-cutanée', tarif: 3.15, type: 'ccam', lien: 'https://www.ameli.fr/accueil-de-la-ccam/trouver-un-acte/fiche-abregee.php?code=QZJA003' },
  { code: 'QZJA001', libelle: 'Injection intraveineuse directe', tarif: 4.73, type: 'ccam', lien: 'https://www.ameli.fr/accueil-de-la-ccam/trouver-un-acte/fiche-abregee.php?code=QZJA001' },
  { code: 'QZJA030', libelle: 'Infiltration thérapeutique', tarif: 10.08, type: 'ccam', lien: 'https://www.ameli.fr/accueil-de-la-ccam/trouver-un-acte/fiche-abregee.php?code=QZJA030' },
  
  // ═══════════════════════════════════════════════════════════
  // ACTES CCAM - PANSEMENTS/SOINS
  // ═══════════════════════════════════════════════════════════
  { code: 'QZFA001', libelle: 'Pansement de lésion unique', tarif: 6.30, type: 'ccam', lien: 'https://www.ameli.fr/accueil-de-la-ccam/trouver-un-acte/fiche-abregee.php?code=QZFA001' },
  { code: 'QZFA006', libelle: 'Ablation de fils ou agrafes', tarif: 4.20, type: 'ccam', lien: 'https://www.ameli.fr/accueil-de-la-ccam/trouver-un-acte/fiche-abregee.php?code=QZFA006' },
  
  // ═══════════════════════════════════════════════════════════
  // ACTES CCAM - SUTURES (PLAIES HORS FACE)
  // ═══════════════════════════════════════════════════════════
  { code: 'QAJA007', libelle: 'Suture plaie profonde < 3 cm (hors face)', tarif: 20.90, type: 'ccam', lien: 'https://www.ameli.fr/accueil-de-la-ccam/trouver-un-acte/fiche-abregee.php?code=QAJA007' },
  { code: 'QAJA002', libelle: 'Suture plaie profonde 3-10 cm (hors face)', tarif: 41.80, type: 'ccam', lien: 'https://www.ameli.fr/accueil-de-la-ccam/trouver-un-acte/fiche-abregee.php?code=QAJA002' },
  { code: 'QAJA001', libelle: 'Suture plaie profonde > 10 cm (hors face)', tarif: 62.70, type: 'ccam', lien: 'https://www.ameli.fr/accueil-de-la-ccam/trouver-un-acte/fiche-abregee.php?code=QAJA001' },
  
  // ═══════════════════════════════════════════════════════════
  // ACTES CCAM - SUTURES (PLAIES FACE)
  // ═══════════════════════════════════════════════════════════
  { code: 'QABA005', libelle: 'Suture plaie superficielle < 3 cm face', tarif: 29.60, type: 'ccam', lien: 'https://www.ameli.fr/accueil-de-la-ccam/trouver-un-acte/fiche-abregee.php?code=QABA005' },
  { code: 'QABA006', libelle: 'Suture plaie superficielle 3-10 cm face', tarif: 50.50, type: 'ccam', lien: 'https://www.ameli.fr/accueil-de-la-ccam/trouver-un-acte/fiche-abregee.php?code=QABA006' },
  
  // ═══════════════════════════════════════════════════════════
  // ACTES CCAM - PETITE CHIRURGIE
  // ═══════════════════════════════════════════════════════════
  { code: 'QZJA004', libelle: 'Ablation lésion superficielle < 1 cm', tarif: 20.90, type: 'ccam', lien: 'https://www.ameli.fr/accueil-de-la-ccam/trouver-un-acte/fiche-abregee.php?code=QZJA004' },
  { code: 'QZJA019', libelle: 'Ablation lésion superficielle 1-2 cm', tarif: 41.80, type: 'ccam', lien: 'https://www.ameli.fr/accueil-de-la-ccam/trouver-un-acte/fiche-abregee.php?code=QZJA019' },
  { code: 'QZJA017', libelle: 'Incision/excision panaris superficiel', tarif: 27.04, type: 'ccam', lien: 'https://www.ameli.fr/accueil-de-la-ccam/trouver-un-acte/fiche-abregee.php?code=QZJA017' },
  { code: 'QAJA003', libelle: 'Excision lésion cutanée profonde < 3 cm', tarif: 41.80, type: 'ccam', lien: 'https://www.ameli.fr/accueil-de-la-ccam/trouver-un-acte/fiche-abregee.php?code=QAJA003' },
  
  // ═══════════════════════════════════════════════════════════
  // ACTES CCAM - GYNÉCOLOGIE
  // ═══════════════════════════════════════════════════════════
  { code: 'JKHD001', libelle: 'Pose de dispositif intra-utérin (DIU)', tarif: 38.40, type: 'ccam', lien: 'https://www.ameli.fr/accueil-de-la-ccam/trouver-un-acte/fiche-abregee.php?code=JKHD001' },
  { code: 'JKLD001', libelle: 'Retrait de dispositif intra-utérin', tarif: 38.40, type: 'ccam', lien: 'https://www.ameli.fr/accueil-de-la-ccam/trouver-un-acte/fiche-abregee.php?code=JKLD001' },
  { code: 'QZLA004', libelle: 'Pose implant contraceptif sous-cutané', tarif: 17.99, type: 'ccam', lien: 'https://www.ameli.fr/accueil-de-la-ccam/trouver-un-acte/fiche-abregee.php?code=QZLA004' },
  { code: 'QZGA004', libelle: 'Retrait implant contraceptif', tarif: 41.80, type: 'ccam', lien: 'https://www.ameli.fr/accueil-de-la-ccam/trouver-un-acte/fiche-abregee.php?code=QZGA004' },
  { code: 'JKQX020', libelle: 'Frottis cervico-utérin', tarif: 12.46, type: 'ccam', lien: 'https://www.ameli.fr/accueil-de-la-ccam/trouver-un-acte/fiche-abregee.php?code=JKQX020' },
  
  // ═══════════════════════════════════════════════════════════
  // ACTES CCAM - ORL
  // ═══════════════════════════════════════════════════════════
  { code: 'CAQK001', libelle: 'Ablation bouchon de cérumen unilatéral', tarif: 25.52, type: 'ccam', lien: 'https://www.ameli.fr/accueil-de-la-ccam/trouver-un-acte/fiche-abregee.php?code=CAQK001' },
  { code: 'GELD004', libelle: 'Lavage de sinus', tarif: 9.60, type: 'ccam', lien: 'https://www.ameli.fr/accueil-de-la-ccam/trouver-un-acte/fiche-abregee.php?code=GELD004' },
  
  // ═══════════════════════════════════════════════════════════
  // ACTES CCAM - OPHTALMOLOGIE
  // ═══════════════════════════════════════════════════════════
  { code: 'BGQP002', libelle: 'Mesure acuité visuelle', tarif: 14.04, type: 'ccam', lien: 'https://www.ameli.fr/accueil-de-la-ccam/trouver-un-acte/fiche-abregee.php?code=BGQP002' },
  { code: 'BFGA004', libelle: 'Ablation corps étranger cornéen superficiel', tarif: 28.80, type: 'ccam', lien: 'https://www.ameli.fr/accueil-de-la-ccam/trouver-un-acte/fiche-abregee.php?code=BFGA004' },
  
  // ═══════════════════════════════════════════════════════════
  // FORFAITS
  // ═══════════════════════════════════════════════════════════
  { code: 'YYYY470', libelle: 'Forfait technique petite chirurgie', tarif: 26.88, type: 'ccam' },
  { code: 'YYYY600', libelle: 'Forfait accueil urgences (régulé)', tarif: 26.88, type: 'ccam' },
];

// Cas cliniques fréquents — construits dynamiquement depuis la source NGAP (backend), G = 30 €
const buildCasCliniques = (m) => {
  const g = m.G ?? 30, vg = m.VG ?? 30, mrt = m.MRT ?? 5, meg = m.MEG ?? 5,
    md = m.MD ?? 10, mdd = m.MDD ?? 29.10, f = m.F ?? 19.06, vrm = m.VRM ?? 66,
    tcg = m.TCG ?? 25, cce = m.CCE ?? 60, gl1 = m.GL1 ?? 60;
  const e = (n) => `${n.toFixed(2).replace('.', ',')} €`;
  return [
    { titre: 'Consultation adulte (médecin traitant)', actes: ['G + MRT'], total: g + mrt, details: `G (${e(g)}) + MRT (${e(mrt)})` },
    { titre: 'Consultation enfant < 6 ans', actes: ['G + MEG'], total: g + meg, details: `G (${e(g)}) + MEG (${e(meg)})` },
    { titre: 'Téléconsultation (secteur 1 / OPTAM)', actes: ['TCG'], total: tcg, details: `TCG (${e(tcg)})` },
    { titre: 'Visite à domicile justifiée', actes: ['VG + MD'], total: vg + md, details: `VG (${e(vg)}) + MD (${e(md)}) + IK selon distance` },
    { titre: 'Visite dimanche / jour férié', actes: ['VG + MDD'], total: vg + mdd, details: `VG (${e(vg)}) + MDD (${e(mdd)})` },
    { titre: 'Consultation cabinet dimanche/férié (garde)', actes: ['G + F'], total: g + f, details: `G (${e(g)}) + F (${e(f)})` },
    { titre: 'Visite régulée nuit profonde (0h-6h)', actes: ['VG + VRM'], total: vg + vrm, details: `VG (${e(vg)}) + VRM (${e(vrm)})` },
    { titre: 'Consultation très complexe (annonce maladie grave)', actes: ['CCE'], total: cce, details: `CCE (${e(cce)})` },
    { titre: 'Consultation longue patient ≥ 80 ans (2026)', actes: ['GL1 / GL2 / GL3'], total: gl1, details: `Consultation longue (${e(gl1)})` },
  ];
};

export const CotationPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedActes, setSelectedActes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  
  // Paramètres de cotation
  const [params, setParams] = useState({
    isMedecinTraitant: true,
    lieu: 'cabinet', // cabinet, domicile
    jour: 'semaine', // semaine, dimanche, ferie
    horaire: 'jour', // jour, soir, nuit
    agePatient: 'adulte', // nourrisson, enfant, adulte, senior
    zone: 'metropole' // metropole, dom
  });

  // Source unique NGAP (backend) + métadonnées de version/révision
  const [ngapTarifs, setNgapTarifs] = useState([]);
  const [ngapMeta, setNgapMeta] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/api/cotation/ngap`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d) { setNgapTarifs(d.tarifs || []); setNgapMeta(d.meta || null); } })
      .catch(() => {});
  }, []);

  const ngapMap = useMemo(() => {
    const map = {};
    ngapTarifs.forEach((t) => { map[t.code] = t.tarif; });
    return map;
  }, [ngapTarifs]);

  const allActes = useMemo(() => {
    const ngapCodes = new Set(ngapTarifs.map((t) => t.code));
    const ngapActes = ngapTarifs.map((t) => ({
      code: t.code,
      libelle: t.libelle,
      tarif: t.tarif,
      type: t.type === 'garde' ? 'urgence' : t.type === 'deplacement' ? 'majoration' : t.type,
    }));
    return [...ngapActes, ...CCAM_ACTES.filter((a) => !ngapCodes.has(a.code))];
  }, [ngapTarifs]);

  const casCliniques = useMemo(() => buildCasCliniques(ngapMap), [ngapMap]);

  const fmtEur = (n, d) => `${(n ?? d).toFixed(2).replace('.', ',')} €`;
  const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('fr-FR') : '—');

  // Recherche d'actes
  const handleSearch = (term) => {
    setSearchTerm(term);
    const t = term.trim();
    if (t.length === 0) {
      setSearchResults([]);
      return;
    }
    const termLower = t.toLowerCase();
    // 1 caractère: on ne matche que par code exact (utile pour les codes courts G, F)
    const results = t.length === 1
      ? allActes.filter((acte) => acte.code.toLowerCase() === termLower)
      : allActes.filter((acte) =>
          acte.code.toLowerCase().includes(termLower) ||
          acte.libelle.toLowerCase().includes(termLower)
        );
    setSearchResults(results);
  };

  // Voice transcription handler
  const handleVoiceTranscription = (text) => {
    setSearchTerm(text);
    handleSearch(text);
  };

  // Ajouter un acte
  const addActe = (acte) => {
    if (!selectedActes.find(a => a.code === acte.code)) {
      setSelectedActes([...selectedActes, acte]);
      toast.success(`${acte.code} ajouté`);
    }
    setSearchTerm('');
    setSearchResults([]);
  };

  // Retirer un acte
  const removeActe = (code) => {
    setSelectedActes(selectedActes.filter(a => a.code !== code));
  };

  // Calculer le total
  const calculateTotal = () => {
    let total = selectedActes.reduce((sum, acte) => sum + (acte.tarif || 0), 0);
    const m = ngapMap;
    // Une seule majoration de déplacement/urgence s'applique selon le contexte
    if (params.lieu === 'domicile') {
      if (params.horaire === 'nuit') total += m.MDI ?? 50;        // nuit profonde 0h-6h
      else if (params.horaire === 'soir') total += m.MDN ?? 45;   // nuit 20h-0h
      else if (params.jour === 'dimanche' || params.jour === 'ferie') total += m.MDD ?? 29.10;
      else total += m.MD ?? 10;                                   // déplacement standard
    } else if (params.jour === 'dimanche' || params.jour === 'ferie') {
      total += m.F ?? 19.06;                                      // urgence cabinet (garde)
    }
    return total.toFixed(2);
  };

  // Demander une suggestion IA
  const askAISuggestion = async () => {
    if (!searchTerm || searchTerm.length < 3) {
      toast.error('Décrivez l\'acte ou la situation clinique');
      return;
    }

    setAiLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/cotation/suggest`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          description: searchTerm,
          params: params
        })
      });

      if (!response.ok) throw new Error('Erreur API');
      
      const data = await response.json();
      setAiSuggestion(data.suggestion);
    } catch (error) {
      toast.error('Erreur lors de la suggestion IA');
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <div data-testid="cotation-page" className="space-y-4 sm:space-y-6">
      {/* Header */}
      <PageHeader icon={Calculator} title="Aide à la Cotation" subtitle="CCAM, NGAP et majorations pour médecins" gradient="from-green-500 to-emerald-600" testId="cotation-header" />

      {/* Sources */}
      <div className="flex flex-wrap gap-2">
        <a 
          href="https://www.ameli.fr/accueil-de-la-ccam/index.php" 
          target="_blank" 
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-xs bg-blue-900/30 text-blue-400 px-3 py-1 rounded-full hover:bg-blue-900/50 transition-colors"
        >
          <ExternalLink className="w-3 h-3" />
          CCAM Ameli (officiel)
        </a>
      </div>

      {/* Métadonnées de version / révision semestrielle des tarifs */}
      {ngapMeta && (
        <div data-testid="cotation-tarifs-meta" className="glassmorphic rounded-xl px-3 py-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-400">
          <span>Tarifs NGAP : <span className="text-slate-200">{ngapMeta.ngap_version}</span></span>
          <span className="text-slate-600">•</span>
          <span className="text-slate-300">{ngapMeta.ccam_version}</span>
          <span className="text-slate-600">•</span>
          <span>MAJ {fmtDate(ngapMeta.ngap_last_review)} — prochaine révision {fmtDate(ngapMeta.ngap_next_review)}</span>
          {ngapMeta.ngap_needs_review && (
            <span data-testid="ngap-review-flag" className="inline-flex items-center gap-1 text-amber-400 font-medium">
              <AlertCircle className="w-3 h-3" /> NGAP à revérifier (source officielle)
            </span>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Colonne 1: Recherche et paramètres */}
        <div className="lg:col-span-2 space-y-4">
          {/* Paramètres de consultation */}
          <div className="glassmorphic rounded-2xl p-4">
            <h3 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
              <Info className="w-4 h-4 text-blue-400" />
              Paramètres de la consultation
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {/* Médecin traitant */}
              <label className="flex items-center gap-2 text-sm text-slate-300">
                <input
                  type="checkbox"
                  checked={params.isMedecinTraitant}
                  onChange={(e) => setParams({...params, isMedecinTraitant: e.target.checked})}
                  className="rounded bg-slate-800 border-slate-600"
                />
                <User className="w-4 h-4 text-slate-500" />
                Médecin traitant
              </label>
              
              {/* Lieu */}
              <select
                value={params.lieu}
                onChange={(e) => setParams({...params, lieu: e.target.value})}
                className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-sm text-slate-300"
              >
                <option value="cabinet">🏥 Cabinet</option>
                <option value="domicile">🏠 Domicile</option>
              </select>
              
              {/* Jour */}
              <select
                value={params.jour}
                onChange={(e) => setParams({...params, jour: e.target.value})}
                className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-sm text-slate-300"
              >
                <option value="semaine">📅 Semaine</option>
                <option value="dimanche">☀️ Dimanche</option>
                <option value="ferie">🎉 Jour férié</option>
              </select>
              
              {/* Horaire */}
              <select
                value={params.horaire}
                onChange={(e) => setParams({...params, horaire: e.target.value})}
                className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-sm text-slate-300"
              >
                <option value="jour">🌞 Jour (8h-20h)</option>
                <option value="soir">🌆 Soir (20h-0h)</option>
                <option value="nuit">🌙 Nuit (0h-6h)</option>
              </select>
              
              {/* Âge patient */}
              <select
                value={params.agePatient}
                onChange={(e) => setParams({...params, agePatient: e.target.value})}
                className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-sm text-slate-300"
              >
                <option value="nourrisson">👶 Nourrisson (&lt;2 ans)</option>
                <option value="enfant">🧒 Enfant (2-6 ans)</option>
                <option value="adulte">👤 Adulte (6-80 ans)</option>
                <option value="senior">👴 Senior (&gt;80 ans)</option>
              </select>
            </div>
          </div>

          {/* Recherche d'actes */}
          <div className="glassmorphic rounded-2xl p-4">
            <h3 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
              <Search className="w-4 h-4 text-green-400" />
              Rechercher un acte
            </h3>
            
            <div className="space-y-3">
              <div className="relative">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  placeholder="Code CCAM, mot-clé ou description de l'acte..."
                  className="w-full bg-slate-800/50 border border-slate-700 rounded-xl px-4 py-3 pl-10 text-slate-200 placeholder:text-slate-500 focus:border-green-500 focus:ring-1 focus:ring-green-500"
                />
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
              </div>
              
              {/* Voice Input & AI Suggestion */}
              <div className="flex gap-2">
                <VoiceInput 
                  onTranscription={handleVoiceTranscription}
                  placeholder="Parler"
                  disabled={aiLoading}
                  className="flex-shrink-0"
                />
                <Button
                  onClick={askAISuggestion}
                  disabled={aiLoading || searchTerm.length < 3}
                  className="flex-1 rounded-xl px-4 py-2.5 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-sm"
                >
                  {aiLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : '✨'} Suggestion IA
                </Button>
              </div>
            </div>

            {/* Résultats de recherche */}
            {searchResults.length > 0 && (
              <div className="mt-3 bg-slate-900/80 rounded-xl border border-slate-700 max-h-60 overflow-y-auto">
                {searchResults.map((acte) => (
                  <button
                    key={acte.code}
                    onClick={() => addActe(acte)}
                    className="w-full px-3 sm:px-4 py-3 flex items-center justify-between gap-2 hover:bg-slate-800/50 active:bg-slate-800/70 transition-colors border-b border-slate-800 last:border-0"
                  >
                    <div className="text-left min-w-0 flex-1">
                      <span className="text-green-400 font-mono text-sm">{acte.code}</span>
                      <p className="text-slate-300 text-sm break-words">{acte.libelle}</p>
                    </div>
                    <span className="text-emerald-400 font-medium whitespace-nowrap shrink-0">{acte.tarif.toFixed(2)}€</span>
                  </button>
                ))}
              </div>
            )}

            {/* Suggestion IA */}
            {aiSuggestion && (
              <div data-testid="cotation-ai-suggestion" className="mt-4 p-2.5 sm:p-4 bg-gradient-to-br from-green-900/30 to-emerald-900/30 rounded-xl border border-green-500/30">
                <div className="flex items-center gap-2 mb-2.5">
                  <span className="text-lg">✨</span>
                  <h4 className="text-green-400 font-medium text-sm">Suggestion IA</h4>
                </div>
                <AIResultSections text={aiSuggestion} accent="emerald" testId="cotation-result-sections" rateFeature="cotation" />
              </div>
            )}
          </div>

          {/* Actes sélectionnés */}
          <div className="glassmorphic rounded-2xl p-4">
            <h3 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
              <FileText className="w-4 h-4 text-emerald-400" />
              Actes sélectionnés
            </h3>
            
            {selectedActes.length === 0 ? (
              <p className="text-slate-500 text-sm text-center py-4">
                Aucun acte sélectionné. Recherchez et ajoutez des actes ci-dessus.
              </p>
            ) : (
              <div className="space-y-2">
                {selectedActes.map((acte) => (
                  <div 
                    key={acte.code}
                    className="flex items-center justify-between gap-2 bg-slate-800/50 rounded-lg px-3 py-2"
                  >
                    <div className="min-w-0 flex-1">
                      <span className="text-green-400 font-mono text-sm">{acte.code}</span>
                      <span className="text-slate-400 text-sm ml-2 break-words">{acte.libelle}</span>
                    </div>
                    <div className="flex items-center gap-2 sm:gap-3 shrink-0">
                      <span className="text-emerald-400 font-medium whitespace-nowrap">{acte.tarif.toFixed(2)}€</span>
                      <button
                        onClick={() => removeActe(acte.code)}
                        aria-label="Retirer l'acte"
                        className="w-9 h-9 flex items-center justify-center rounded-lg text-rose-400 hover:text-rose-300 hover:bg-rose-500/10 text-sm"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                ))}
                
                {/* Total */}
                <div className="flex items-center justify-between pt-3 border-t border-slate-700 mt-3">
                  <span className="text-white font-medium">Total estimé</span>
                  <span className="text-2xl font-bold text-emerald-400">{calculateTotal()}€</span>
                </div>
                
                {/* Majorations automatiques */}
                {(params.jour !== 'semaine' || params.horaire !== 'jour') && (
                  <div className="mt-2 p-2 bg-amber-900/20 rounded-lg border border-amber-500/30">
                    <p className="text-amber-400 text-xs">
                      ⚠️ Majoration de déplacement incluse :
                      {params.lieu === 'domicile'
                        ? (params.horaire === 'nuit' ? ' MDI (nuit 0h-6h)'
                          : params.horaire === 'soir' ? ' MDN (soir 20h-0h)'
                          : (params.jour !== 'semaine' ? ' MDD (dimanche/férié)' : ' MD (déplacement)'))
                        : (params.jour !== 'semaine' ? ' F (dimanche/férié, cabinet garde)' : ' —')}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Colonne 2: Cas cliniques fréquents */}
        <div className="space-y-4">
          <div className="glassmorphic rounded-2xl p-4">
            <h3 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
              <BookOpen className="w-4 h-4 text-blue-400" />
              Cas cliniques fréquents
            </h3>
            
            <div className="space-y-2 max-h-[500px] overflow-y-auto pr-2">
              {casCliniques.map((cas) => (
                <div 
                  key={cas.titre}
                  className="bg-slate-800/50 rounded-lg p-3 hover:bg-slate-800/80 transition-colors cursor-pointer"
                >
                  <div className="flex items-start justify-between mb-1">
                    <h4 className="text-slate-200 text-sm font-medium">{cas.titre}</h4>
                    <span className="text-emerald-400 font-bold text-sm whitespace-nowrap ml-2">
                      {typeof cas.total === 'number' ? `${cas.total.toFixed(2)}€` : cas.total}
                    </span>
                  </div>
                  <p className="text-green-400 font-mono text-xs mb-1">{cas.actes.join(' + ')}</p>
                  <p className="text-slate-500 text-xs">{cas.details}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Aide rapide */}
          <div className="glassmorphic rounded-2xl p-4">
            <h3 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-amber-400" />
              Rappels importants
            </h3>
            <ul className="text-xs text-slate-400 space-y-2">
              <li>• <span className="text-amber-400">MRT</span> : +{fmtEur(ngapMap.MRT, 5)} si médecin traitant (≥16 ans)</li>
              <li>• <span className="text-amber-400">MEG</span> : +{fmtEur(ngapMap.MEG, 5)} enfant &lt;6 ans</li>
              <li>• <span className="text-amber-400">MD</span> : +{fmtEur(ngapMap.MD, 10)} déplacement (visite justifiée)</li>
              <li>• <span className="text-amber-400">MDD</span> : +{fmtEur(ngapMap.MDD, 29.10)} visite dimanche/férié</li>
              <li>• <span className="text-amber-400">F</span> : +{fmtEur(ngapMap.F, 19.06)} urgence dimanche/férié (cabinet)</li>
              <li>• <span className="text-amber-400">VRM</span> : +{fmtEur(ngapMap.VRM, 66)} visite régulée nuit profonde</li>
              <li>• Actes CCAM : règle du 1/2 tarif si acte associé</li>
              <li>• Visite domicile : ajouter IK (indemnité km)</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Avertissement */}
      <div className="glassmorphic rounded-xl p-4 border-l-4 border-amber-500">
        <p className="text-xs text-slate-400">
          <span className="text-amber-400 font-medium">⚖️ Avertissement :</span> Cette aide à la cotation est fournie à titre indicatif. 
          Les tarifs peuvent évoluer. Référez-vous toujours à la CCAM officielle et aux textes en vigueur. 
          Le médecin reste responsable de sa cotation.
        </p>
      </div>
    </div>
  );
};
